/**
 * Flight Engine V2 — ViewModel shapes (Wave 1).
 * Pure data contracts for mappers. No business logic.
 */

/** @typedef {'unknown'|'included'|'notIncluded'|'availableForPurchase'|'selectedPaid'|'unavailable'} BaggageStatus */

/**
 * @typedef {object} BaggageViewModel
 * @property {BaggageStatus} status
 * @property {'carryOn'|'checked'|'unknown'|null} [kind]
 * @property {string|null} [passengerId]
 * @property {number|null} [segmentIndex]
 * @property {number|null} [quantity]
 * @property {number|null} [weightKg]
 * @property {string|null} [currency]
 * @property {number|null} [price]
 * @property {string|null} [label]
 */

/**
 * @typedef {object} FareViewModel
 * @property {string|null} cabin
 * @property {string|null} fareFamily
 * @property {string|null} fareType
 * @property {boolean|null} charter
 * @property {string|null} changePolicy
 * @property {string|null} cancelPolicy
 * @property {string|null} refundability
 * @property {string|null} ticketingDeadline
 */

/**
 * @typedef {object} OfferCapabilitiesViewModel
 * @property {boolean} [canBook]
 * @property {boolean} [canHold]
 * @property {boolean} [canPay]
 * @property {boolean} [canAddBaggage]
 * @property {boolean} [supportsMultipleFares]
 * @property {boolean} [requiresPassengerPassport]
 * @property {boolean} [requiresReprice]
 * @property {boolean} [supportsSplitPayment]
 */

/**
 * @typedef {object} FlightSegmentViewModel
 * @property {string|null} id
 * @property {string|null} direction
 * @property {string|null} originCode
 * @property {string|null} destinationCode
 * @property {string|null} departureTime
 * @property {string|null} arrivalTime
 * @property {string|null} airlineCode
 * @property {string|null} flightNumber
 * @property {number|null} durationMinutes
 * @property {number|null} stopCount
 * @property {string|null} [operatingCarrier]
 * @property {string|null} [cabin]
 * @property {string|null} [aircraft]
 * @property {string|null} [departureAirport]
 * @property {string|null} [arrivalAirport]
 */

/**
 * Pricing Fx vs persisted booking snapshot — never conflate.
 * @typedef {object} BookingPriceViewModel
 * @property {string|null} sourceCurrency
 * @property {number|null} flightBaseTotal
 * @property {number|null} baggageTotal
 * @property {number|null} extrasTotal
 * @property {string|null} [extrasCurrency] — denomination of extrasTotal (ILS pipeline, or provider currency)
 * @property {number|null} sourceCurrencyTotal — supplier/raw fare (often rawTotalFare). Not the customer transaction total.
 * @property {number|null} [clientFlightTotal] — customer fare in source currency (priceClientTotalUsd, else per-pax sum, else customer per-person × pax). Null for proven mixed fares without those sources. Never rawTotalFare.
 * @property {number|null} pricingFxRate
 * @property {number|null} bookingExchangeRateSnapshot
 * @property {number|null} calculatedIlsTotal — agent display, legacyAppRound2 (ceil) of the customer ILS total
 * @property {number|null} roundingAdjustment
 * @property {number|null} payableIlsTotal — engine charge (pricing.total). Not re-ceiled for the summary.
 * @property {number|null} [summaryPayableIlsTotal] — summary לתשלום: engine pricing.total when it already includes extras, otherwise calculatedIlsTotal + handling fee
 * @property {number|null} handlingFee
 * @property {number|null} creditCardFee
 * @property {object|null} [passengerPriceBreakdown]
 * @property {object[]|null} [baggageBreakdown]
 * @property {object|null} [commission]
 * @property {string|null} [pricingSource]
 * @property {object|null} [display] — from resolveAgentFlightDisplayedSortPrice
 */

/**
 * @typedef {object} FlightOfferViewModel
 * @property {string} id
 * @property {string|null} routeLabel
 * @property {FlightSegmentViewModel[]} segments
 * @property {FareViewModel} fare
 * @property {BaggageViewModel[]} baggage
 * @property {OfferCapabilitiesViewModel} capabilities
 * @property {BookingPriceViewModel} price
 * @property {string|null} airlineSummary
 * @property {object|null} [cardMeta]
 */

/**
 * @typedef {object} FlightSearchViewModel
 * @property {string|null} tripType
 * @property {string|null} origin
 * @property {string|null} destination
 * @property {string|null} departureDate
 * @property {string|null} returnDate
 * @property {number|null} adults
 * @property {number|null} children
 * @property {number|null} infants
 * @property {string|null} cabin
 */

export const BAGGAGE_STATUS = Object.freeze({
  UNKNOWN: "unknown",
  INCLUDED: "included",
  NOT_INCLUDED: "notIncluded",
  AVAILABLE_FOR_PURCHASE: "availableForPurchase",
  SELECTED_PAID: "selectedPaid",
  UNAVAILABLE: "unavailable",
});
